import React from 'react';
import PropTypes from 'prop-types';
import IconButton from 'material-ui/IconButton';
import { DragSource } from 'react-dnd';
import moment from 'moment';
import momentDuration from 'moment-duration-format';
import Popover from '@material-ui/core/Popover';
import { PopoverStyle } from '../../../theme/AllocateBaseTheme';
import TimelineAssignmentPopover from '../../../containers/DailyPlanning/TimelineAssignment/TimelineAssignmentPopoverContainer';
import { durationToPixels, refSetter, cardWidthToPixels } from '../../../utils/helpers';
import globals from '../../../globals/communityGlobals';
import { cellLengthInMinutes } from '../../../settings/scheduler';
import { cardTypes } from '../../../settings/constants/cardTypes';
import { AssignmentType, AssignmentVisitState, AssignmentVisitSchedule, TreatmentInstanceStatus } from '../../../enums';
import { parseDateUtc } from '../../../utils/dates';

momentDuration(moment);

const locale = globals.getArea('dailyPlanning');

export const ASSIGNMENT = 'ASSIGNMENT';

function canDrag(startDate, treatmentInstanceStatus) {
  // Prevent dragging of assignments in the past
  const utcToday = globals.now.utcOffset(0, true);
  const start = moment(startDate);
  if (start.isSameOrBefore(utcToday.clone().startOf('day'))) {
    return false;
  }
  if (treatmentInstanceStatus === TreatmentInstanceStatus.Closed) {
    return false;
  }
  return (
    treatmentInstanceStatus === null ||
    treatmentInstanceStatus === TreatmentInstanceStatus.ToDo ||
    start.isAfter(utcToday)
  );
}

const assignmentSource = {
  canDrag({ assignment }) {
    const { startDate, treatmentInstanceStatus } = assignment;
    return canDrag(startDate, treatmentInstanceStatus);
  },

  isDragging(props, monitor) {
    const { assignGuid } = monitor.getItem();
    return assignGuid === props.assignGuid;
  },

  beginDrag({ assignGuid, assignment, getTravelTimesByPostcode, getPostcodeDistance }, monitor, component) {
    const {
      requiredGrade,
      activitiesTotalDuration,
      isSpecificTime,
      isMasterAssigned,
      effectiveTimeSlots,
      assignedCopiesCount,
      postcode,
      masterId,
      requiredSkills,
      excludedClinicians,
      treatmentId,
    } = assignment;
    getTravelTimesByPostcode(postcode);
    getPostcodeDistance(postcode);
    return {
      assignGuid,
      previewHtml: component.preview.outerHTML,
      requiredGrade,
      requiredTime: activitiesTotalDuration,
      effectiveTimeSlots,
      schedulingDisabled: assignedCopiesCount !== 0,
      isSpecificTime,
      masterId,
      isMasterAssigned,
      requiredSkills,
      excludedClinicians,
      treatmentId,
    };
  },

  async endDrag(props, monitor) {
    if (!monitor.didDrop()) {
      return;
    }
    const dropResult = monitor.getDropResult();
    const { jwtToken } = props;
    try {
      let result;
      let id;
      let newPostingId;
      let responseFromCopies;
      const doesHaveCopies = props.assignment.totalCopiesCount > 0;
      if (dropResult && dropResult.postingId) {
        newPostingId = dropResult.postingId;
        result = await props.rescheduleAssignment(
          props.assignGuid,
          props.postingId,
          dropResult.postingId,
          dropResult.startDate,
        );
        id = result.response.data.newAssignmentId;
      } else {
        responseFromCopies = await props.getCopiesPostings(props.assignment.id);
        result = await props.removeAssignment(props.assignment.id, props.assignGuid, props.postingId);
        id = props.assignment.id;
        newPostingId = 0;
      }
      if (result.response.status === 200) {
        if (result.type === 'ASSIGNMENT_RESCHEDULE_SUCCESS') {
          const isPatientAssignment = props.assignment.assignmentType === AssignmentType.PatientAssignment;
          props.refreshSchedulerAfterReschedule(
            props.assignGuid,
            id,
            props.postingId,
            newPostingId,
            props.assignment.visitStartDate,
            props.assignment.treatmentInstanceId,
            props.teamId,
            isPatientAssignment,
            doesHaveCopies,
            jwtToken,
          );
        } else {
          const postingsIds = [...responseFromCopies.response.data.postingIds];
          props.refreshSchedulerAfterUnassign(
            props.assignGuid,
            id,
            postingsIds.concat(props.postingId),
            props.assignment.startDate,
            props.assignment.activitiesTotalDuration,
            props.teamId,
            props.assignment.treatmentInstanceId,
            jwtToken,
          );
        }
      }
    } catch (error) {
      // Log errors
      // eslint-disable-next-line no-console
      console.error(error.response ? error.response.data : error);
    }
  },
};

function collect(connect, monitor) {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDraggable: monitor.canDrag(),
    isDragging: monitor.isDragging(),
    isAnythingDragged: monitor.getItemType() !== null,
  };
}

const styles = {
  iconTwilight: { display: 'none' },
  anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
  targetOrigin: { horizontal: 'left', vertical: 'top' },
  popover: { width: '400px' },
};
const defaultTimeSlots = [];
class TimelineAssignment extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      travelExpanded: false,
      infoExpanded: false,
      open: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.isAnythingDragged) {
      this.setState({
        travelExpanded: false,
        infoExpanded: false,
      });
    }
  }

  componentDidUpdate() {
    if (this.state.travelExpanded) {
      document.addEventListener('click', this.hideTravel);
    } else if (!this.state.travelExpanded) {
      document.removeEventListener('click', this.hideTravel);
    }

    if (this.state.infoExpanded) {
      document.addEventListener('click', this.hideInfo);
    } else if (!this.state.infoExpanded) {
      document.removeEventListener('click', this.hideInfo);
    }
  }

  setRef = refSetter.bind(this);

  getIconInfoStyle = assignmentType => ({
    display: assignmentType !== AssignmentType.PatientAssignment ? 'none' : null,
  });

  getAssignmentInfoWrapperStyle = (assignment, travelTime) => {
    let marginLeftAssignment = 0;
    if (assignment.visitStarted !== null) {
      const offset = moment(assignment.visitStarted).minutes() % cellLengthInMinutes;
      if (offset > 0 && offset < cellLengthInMinutes) {
        marginLeftAssignment = durationToPixels(offset - cellLengthInMinutes);
      }
    } else {
      marginLeftAssignment = assignment.visitStartDate !== assignment.startDate ? `${durationToPixels(travelTime)}` : 0;
    }
    return { marginLeft: marginLeftAssignment };
  };

  getInfoStyle = (allowedDuration, startMarginDuration) => {
    const infoWidth = durationToPixels(allowedDuration);
    return {
      marginLeft: startMarginDuration > 0 ? durationToPixels(startMarginDuration) : 0,
      minWidth: infoWidth,
      width: this.state.infoExpanded ? 'auto' : infoWidth,
      zIndex: this.state.infoExpanded && '5',
      transition: 'width 2s',
    };
  };

  getAssignmentNodeStyle = () => ({
    zIndex: this.state.infoExpanded && '5',
    marginLeft: `-${durationToPixels(this.props.outOfAvailabilityOffset)}`,
  });

  getPositiveTravelTimeStyle = (isAnythingDragged, visitStarted, travelTime, travelExpanded, marginLeft) => ({
    marginLeft: `${durationToPixels(marginLeft)}`,
    visibility: isAnythingDragged || visitStarted !== null ? 'hidden' : undefined,
    maxWidth: `${durationToPixels(travelTime)}`,
    width: `${durationToPixels(travelTime)}`,
    marginTop: 1,
    zIndex: travelExpanded && '5',
  });

  getAssignmentInfoStyle = () => ({
    backgroundColor:
      moment.duration(this.props.assignment.travelStaggering, 'hours').asSeconds() !== 0 ? '#de3233' : null,
  });

  getDuration = (plannedStart, visitStart, plannedEnd, visitEnded, schedulerModeStartTime, schedulerModeEndTime) => {
    const plannedStartDate =
      visitStart.diff(schedulerModeStartTime, 'minutes') <= 0 ? schedulerModeStartTime : visitStart;
    const plannedEndDate = plannedEnd.diff(schedulerModeEndTime, 'minutes') >= 0 ? schedulerModeEndTime : plannedEnd;

    if (visitEnded) {
      const visitEndDate =
        parseDateUtc(visitEnded).diff(schedulerModeEndTime, 'minutes') >= 0 ? schedulerModeEndTime : visitEnded;
      const realEnd = parseDateUtc(visitEndDate);
      if (realEnd.isBefore(plannedStartDate, 'minute')) {
        return cellLengthInMinutes;
      }

      return { allowedDuration: realEnd.diff(plannedStartDate, 'minutes'), visitStartMargin: 0, travelStartMargin: 0 };
    }

    const realDuration = plannedEnd.diff(visitStart, 'minutes');

    const allowedDuration = plannedEndDate.diff(plannedStartDate, 'minutes');
    const visitStartMargin =
      plannedStart.diff(schedulerModeStartTime, 'minutes') <= 0
        ? schedulerModeStartTime.diff(plannedStart, 'minutes')
        : 0;
    const travelStartMargin =
      plannedStart.diff(schedulerModeStartTime, 'minutes') <= 0
        ? schedulerModeStartTime.diff(plannedStart, 'minutes')
        : 0;

    return { realDuration, allowedDuration, visitStartMargin, travelStartMargin };
  };

  getAllowedTravelDuration = (plannedStart, visitStart, schedulerModeStartTime, schedulerModeEndTime) => {
    const isBeforeStart = plannedStart.diff(schedulerModeStartTime, 'minutes') < 0;
    return isBeforeStart
      ? visitStart.diff(schedulerModeStartTime, 'minutes')
      : schedulerModeEndTime.diff(plannedStart, 'minutes');
  };

  getTravelDuration = (plannedStart, visitStart, schedulerModeStartTime, schedulerModeEndTime) => {
    if (!this.isValidPeriod(plannedStart, visitStart, schedulerModeStartTime, schedulerModeEndTime)) return 0;

    const realDuration = visitStart.diff(plannedStart, 'minutes');
    const allowedTravelDuration = this.getAllowedTravelDuration(
      plannedStart,
      visitStart,
      schedulerModeStartTime,
      schedulerModeEndTime,
    );

    return allowedTravelDuration < realDuration ? allowedTravelDuration : realDuration;
  };

  hasVisit = (visitStart, visitEnd, schedulerModeStartTime, schedulerModeEndTime) =>
    visitStart.isBetween(schedulerModeStartTime, schedulerModeEndTime, null, '[)') ||
    visitEnd.isBetween(schedulerModeStartTime, schedulerModeEndTime, null, '(]') ||
    (parseDateUtc(visitStart).isBefore(schedulerModeStartTime) && parseDateUtc(visitEnd).isAfter(schedulerModeEndTime));

  calculateDurations = (assignment, schedulerStartTime, schedulerEndTime) => {
    const { visualEndDate, visitStarted, visitEnded, visitStartDate, startDate } = assignment;
    const schedulerModeStartTime = parseDateUtc(schedulerStartTime);
    const schedulerModeEndTime = parseDateUtc(schedulerEndTime);

    const plannedStart = parseDateUtc(startDate);
    const visualEnd = parseDateUtc(visualEndDate);
    const visitStart = parseDateUtc(visitStarted || visitStartDate);
    const travelTime = this.getTravelDuration(plannedStart, visitStart, schedulerModeStartTime, schedulerModeEndTime);
    const hasVisit = this.hasVisit(visitStart, visualEnd, schedulerModeStartTime, schedulerModeEndTime);

    const { allowedDuration, visitStartMargin, travelStartMargin } = this.getDuration(
      plannedStart,
      visitStart,
      visualEnd,
      visitEnded,
      schedulerModeStartTime,
      schedulerModeEndTime,
    );

    return {
      allowedDuration,
      visitStartMargin,
      travelStartMargin: travelTime > 0 ? travelStartMargin : 0,
      travelTime,
      hasVisit,
      hasTravel: travelTime > 0,
    };
  };

  handleInfoClick = event => {
    // This prevents ghost click.
    event.preventDefault();
    this.setState({
      open: true,
      anchorEl: event.currentTarget,
    });
  };

  handleRequestClose = () => {
    this.setState({
      open: false,
    });
  };

  toggleVisibility = element => () => {
    const propName = `${element}Expanded`;
    this.setState({
      [propName]: !this.state[propName],
    });
  };

  hideFunction = (element, e) => {
    e.stopPropagation();
    if (this[`${element}Toggle`] && !this[`${element}Toggle`].contains(e.target)) {
      this.collapseSpecific(element);
    }
  };

  hideTravel = e => {
    this.hideFunction('travel', e);
  };

  hideInfo = e => {
    this.hideFunction('info', e);
  };

  collapseSpecific = element => {
    this.setState({
      [`${element}Expanded`]: false,
    });
  };

  isLocked = () => {
    const { hasAssignedCopies, treatmentInstanceStatus } = this.props.assignment;
    return hasAssignedCopies || treatmentInstanceStatus === TreatmentInstanceStatus.InProgress;
  };

  travelLabel = () => {
    const { travelStaggering } = this.props.assignment;
    const timeLabel = travelStaggering
      ? moment.duration(travelStaggering, 'hours').format(globals.durationFormats.travel)
      : '';

    return `${locale.getMsg('travelStaggering')}: ${timeLabel}`;
  };

  vistitLabel = () => {
    const { visitStartDate, endDate } = this.props.assignment;
    const visitStartedLabel = visitStartDate
      ? moment(visitStartDate)
          .utcOffset(0, true)
          .format(globals.timeFormats.short)
      : '';
    const visitEndedLabel = endDate
      ? moment(endDate)
          .utcOffset(0, true)
          .format(globals.timeFormats.short)
      : '';

    let timeLabel = visitStartedLabel;
    timeLabel = `${timeLabel} - ${visitEndedLabel}`;

    return `${locale.getMsg('planned')}: ${timeLabel}`;
  };

  isValidPeriod = (startTime, endTime, startRangeDate, endRangeDate) =>
    startTime.isBetween(startRangeDate, endRangeDate, null, '[)') ||
    endTime.isBetween(startRangeDate, endRangeDate, null, '(]') ||
    (parseDateUtc(startTime).isBefore(startRangeDate) && parseDateUtc(endTime).isAfter(endRangeDate));

  isWithInValidPeriod = (startTime, endTime, startRangeDate, endRangeDate) =>
    startTime.isBetween(startRangeDate, endRangeDate, null, '[)') &&
    endTime.isBetween(startRangeDate, endRangeDate, null, '(]');

  visitTime = () => {
    const { visitStarted, visitStatus } = this.props.assignment;

    if (visitStarted === null || visitStatus === AssignmentVisitState.OnTime) {
      return '';
    }

    return (
      <IconButton
        className="timeline-icon-button visit-time"
        iconClassName="allocateIcons timeline-icon"
        disableTouchRipple
        tooltipPosition="top-right"
        title={this.vistitLabel()}
      >
        access_time
      </IconButton>
    );
  };

  renderLock = tooltipMsg => (
    <span className="icon-lock icon-svg asw-icon" title={locale.getMsg(tooltipMsg)} role="button" />
  );

  render() {
    const { infoExpanded, travelExpanded } = this.state;
    const {
      canAllocateWork,
      assignment,
      connectDragSource,
      isDragging,
      isAnythingDragged,
      assignGuid,
      schedulerModeStartTime,
      schedulerModeEndTime,
    } = this.props;
    const {
      primaryLabel,
      visitStarted,
      visitEnded,
      visitStartDate,
      visitStatus,
      assignedCopiesCount,
      totalCopiesCount,
      isCollapsed,
      isOverlapping,
      endDate,
      secondaryLabel,
      isReviewDate,
      visitSchedule,
      cardType,
      assignmentType,
      postcode,
      ragStatus,
      isWithinMileageCap,
    } = assignment;

    const visitStart = parseDateUtc(visitStarted || visitStartDate);
    const startLabel = visitStart.format(globals.timeFormats.short);
    const endLabel = parseDateUtc(visitEnded || endDate).format(globals.timeFormats.short);

    let timeLabel = `${startLabel} - ${endLabel}`;
    if (assignment.visitStarted !== null && assignment.visitEnded === null) {
      const currEnd = moment(assignment.visitStarted)
        .add(assignment.activitiesTotalDuration, 'minutes')
        .utcOffset(0, true)
        .format(globals.timeFormats.short);
      timeLabel = `${startLabel} - ${currEnd}`;
    }

    const {
      allowedDuration,
      visitStartMargin,
      travelStartMargin,
      travelTime,
      hasTravel,
      hasVisit,
    } = this.calculateDurations(assignment, schedulerModeStartTime, schedulerModeEndTime);

    const cardWidth = cardWidthToPixels(allowedDuration);
    const infoExpand = infoExpanded ? ' card-open expand' : ' card-closed';

    let lock;
    let isMasterRow;
    let iconHide;
    let assignmentInfoClass = '';
    let schedule = '';
    let copy = '';
    let overlapping = '';
    let retrospective = '';
    if (cardType === cardTypes.ADDITIONAL_CLINICIAN || cardType === cardTypes.OVERRIDDEN_CLINICIAN) {
      copy = '-copy';
    }
    if (visitSchedule === AssignmentVisitSchedule.SpecificTime) {
      schedule = '-fixed';
    } else if (visitSchedule === AssignmentVisitSchedule.SingleTimeSlot) {
      schedule = '-timeslot';
    } else if (visitSchedule === AssignmentVisitSchedule.MultipleTimeSlots) {
      schedule = '-multipletimeslot';
    }
    if (isOverlapping === true) {
      overlapping = '-overlapping';
    }

    if (cardType === cardTypes.RETROSPECTIVE_VISIT) {
      retrospective = '-retro';
    }
    const assignmentStatusClass = `asw-event-card--${visitStatus.toLowerCase()}${schedule}${copy}${retrospective}${overlapping}${
      isCollapsed ? '-overlap' : ''
    }`;

    if (assignmentType === AssignmentType.NonPatientAssignment) {
      isMasterRow = <span className="icon-person-type icon-svg asw-icon" />;
      assignmentInfoClass = ' assignment-info assignment-npa';
    } else if (assignment.isMasterOrReview && totalCopiesCount !== 0) {
      if (assignment.hasAssignedCopies) {
        isMasterRow = (
          <span className={`icon-person-type-${ragStatus.name.toLowerCase()} icon-svg asw-icon nm`}>
            <span className="copyCount">{`+${assignedCopiesCount}`}</span>
          </span>
        );
      } else {
        isMasterRow = <span className={`icon-person-type-${ragStatus.name.toLowerCase()} + icon-svg asw-icon`} />;
      }
      assignmentInfoClass = ' assignment-info assignment-master';
    } else if (assignment.isMasterOrReview && totalCopiesCount === 0) {
      isMasterRow = <span className={`icon-person-type-${ragStatus.name.toLowerCase()}  icon-svg asw-icon`} />;
      assignmentInfoClass = ' assignment-info assignment-master';
    } else if (!assignment.isMasterOrReview) {
      isMasterRow = <span className={`icon-person-type-${ragStatus.name.toLowerCase()}  icon-svg asw-icon`} />;
      assignmentInfoClass = ' assignment-info assignment-copy';
    }

    if (cardWidth <= 50) {
      iconHide = ' showTwoicons ';
    } else if (cardWidth <= 33) {
      iconHide = ' hideicons ';
    } else {
      iconHide = ' showicons ';
    }

    if (this.isLocked()) {
      if (assignment.hasAssignedCopies) {
        lock = this.renderLock('assignmentHasAssignedCopies');
      } else {
        lock = this.renderLock('assignmentHasStarted');
      }
    }

    const assigmentInfo = (
      <div
        className={`assignment-info-wrapper ${assignmentStatusClass + infoExpand}`}
        style={this.getAssignmentInfoWrapperStyle(assignment, travelTime)}
      >
        {hasVisit && (
          <div
            role="presentation"
            className={
              assignmentInfoClass +
              infoExpand +
              iconHide +
              (assignmentType !== AssignmentType.PatientAssignment ? 'npa' : null)
            }
            onClick={this.toggleVisibility('info')}
            style={this.getInfoStyle(allowedDuration, visitStartMargin)}
            ref={this.setRef('infoToggle')}
          >
            <div className="patient-details">
              <div className="details">
                <div className="assignment-patient-name">{secondaryLabel}</div>
                <div className="assignment-activities">{primaryLabel}</div>
              </div>
              <div className="icon-container">
                <span
                  className="icon-info icon-svg asw-icon "
                  style={this.getIconInfoStyle(assignmentType)}
                  role="button"
                  onClick={this.handleInfoClick}
                  tabIndex={0}
                />
                {!isWithinMileageCap && (
                  <span
                    className="allocateIcons icon icon-svg asw-icon icon-mileage-fix"
                    style={{ color: '#000', fontSize: '20px', padding: '0 2px' }}
                  >
                    track_changes
                  </span>
                )}
                {isMasterRow}
                {isReviewDate === true ? <span className="icon-review icon-svg asw-icon" /> : ''}
                {lock}
                <span className="icon-twilight icon-svg asw-icon" role="button" style={styles.iconTwilight} />
                <span className="icon-lunch icon-svg asw-icon" role="button" />
                <span className="icon-travel icon-svg asw-icon" role="button" />
                <span className="assignment-time">
                  <span>
                    {this.visitTime()}
                    {timeLabel}
                  </span>
                  <span>{assignmentType !== AssignmentType.PatientAssignment ? postcode : null}</span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
    const assignmentNodeClass = `${visitStatus.toLowerCase()} content ${isDragging ? 'dragging' : ''} `;
    const positiveTravelTimeClass = `travel asw-event-card--travel${isCollapsed ? '-overlap' : ''}${
      isOverlapping ? '-overlapping' : ''
    } `;
    const assignmentNode = (
      <div className={assignmentNodeClass} style={this.getAssignmentNodeStyle()}>
        {hasTravel && (
          <div
            className={positiveTravelTimeClass}
            style={this.getPositiveTravelTimeStyle(
              isAnythingDragged,
              visitStarted,
              travelTime,
              travelExpanded,
              travelStartMargin,
            )}
          >
            <div className="assignment-info" style={this.getAssignmentInfoStyle()}>
              <div className="icon-container">
                <span className="icon-travel icon-svg asw-icon" role="button" title={this.travelLabel()} />
              </div>
            </div>
          </div>
        )}
        {canAllocateWork ? connectDragSource(assigmentInfo, { dropEffect: 'move' }) : assigmentInfo}
      </div>
    );
    const timelineAssignGuid = `timelineAssignGuid${assignGuid}`;
    const ref = this.setRef('preview');
    return (
      <div ref={ref} id={timelineAssignGuid} className="assignment">
        {assignmentNode}
        <Popover
          open={this.state.open}
          anchorEl={this.state.anchorEl}
          anchorOrigin={styles.anchorOrigin}
          transformOrigin={styles.targetOrigin}
          onClose={this.handleRequestClose}
          style={PopoverStyle}
        >
          <TimelineAssignmentPopover
            treatmentInstanceId={assignment.treatmentInstanceId}
            assignmentId={assignment.id}
            timeslots={defaultTimeSlots}
            description={''}
          />
        </Popover>
      </div>
    );
  }
}

TimelineAssignment.defaultProps = {
  isDraggable: false,
};

TimelineAssignment.propTypes = {
  canAllocateWork: PropTypes.bool.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  isAnythingDragged: PropTypes.bool.isRequired,
  assignment: PropTypes.shape({
    postcode: PropTypes.string,
    assignmentType: PropTypes.string,
    travelStaggering: PropTypes.string,
    isReviewDate: PropTypes.bool,
    duration: PropTypes.number,
    startDate: PropTypes.string,
    visitStartDate: PropTypes.string,
    visitStatus: PropTypes.string,
    visitStarted: PropTypes.string,
    visitEnded: PropTypes.string,
    endDate: PropTypes.string,
    effectiveTimeSlots: PropTypes.arrayOf(
      PropTypes.shape({
        from: PropTypes.string,
        to: PropTypes.string,
      }),
    ),
    activitiesTotalDuration: PropTypes.number,
    hasAssignedCopies: PropTypes.bool,
    treatmentInstanceStatus: PropTypes.string,
  }).isRequired,
  isDragging: PropTypes.bool.isRequired,
  // Used in drag and drop
  /* eslint-disable react/no-unused-prop-types */
  assignGuid: PropTypes.number.isRequired,
  /* eslint-enable */
  outOfAvailabilityOffset: PropTypes.number.isRequired,
  // jwtToken: PropTypes.string.isRequired,
  schedulerModeStartTime: PropTypes.string.isRequired,
  schedulerModeEndTime: PropTypes.string.isRequired,
};

export default DragSource(ASSIGNMENT, assignmentSource, collect)(TimelineAssignment);
